import type { ReactNode } from 'react'

import type { PacingStatus, Severity } from '@/api/types'

export type BadgeTone = 'neutral' | 'accent' | 'positive' | 'negative' | 'warning' | 'critical' | 'info'

/** A compact status label. The text carries the meaning; colour only reinforces it. */
export function Badge({
  tone = 'neutral',
  dot = false,
  children,
}: {
  tone?: BadgeTone
  dot?: boolean
  children: ReactNode
}) {
  return (
    <span className="ci-badge" data-tone={tone}>
      {dot ? <span className="ci-badge__dot" aria-hidden="true" /> : null}
      {children}
    </span>
  )
}

const SEVERITY: Record<Severity, { tone: BadgeTone; label: string }> = {
  critical: { tone: 'critical', label: 'Critical' },
  high: { tone: 'negative', label: 'High' },
  medium: { tone: 'warning', label: 'Medium' },
  low: { tone: 'info', label: 'Low' },
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  const { tone, label } = SEVERITY[severity]
  return (
    <Badge tone={tone} dot>
      {label}
    </Badge>
  )
}

const PACING: Record<PacingStatus, { tone: BadgeTone; label: string }> = {
  ahead: { tone: 'positive', label: 'Ahead' },
  on_track: { tone: 'accent', label: 'On track' },
  behind: { tone: 'warning', label: 'Behind' },
}

export function PacingBadge({ status }: { status: PacingStatus }) {
  const { tone, label } = PACING[status]
  return <Badge tone={tone}>{label}</Badge>
}
